

































































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import DateField from "@/components/misc/DateField.vue";
import { saveFile } from "@/shared/saveFile";
import { Getter } from "vuex-class";
import { EVisitorIntegrationModel } from "@/shared/models/eVisitor";
import {
  EVisitorIntegrationEnum,
  EVisitorIntegrationNameEnum,
} from "@/shared/models/enums.type";
import EVisitorReportForm from "./forms/EVisitorReportForm.vue";
import ETurizemReportForm from "./forms/ETurizemReportForm.vue";
import { ResponseType } from "axios";
import { Notification } from "@/store/modules/notifications";
import LoadingOverlay from "../misc/LoadingOverlay.vue";
import brokenRuleAdapter from "@/shared/handleBrokenRuleAdapter";

export enum EVisitorReportTypeEnum {
  Monthly = 1,
  Yearly = 2,
}

@Component({
  components: {
    DateField,
    LoadingOverlay,
    EVisitorReportForm,
    ETurizemReportForm,
  },
})
export default class EVisitorReportDialog extends Vue {
  dialogOpen = false;
  loading = false;

  eVisitorReportTypeEnum = EVisitorReportTypeEnum;
  reportType = EVisitorReportTypeEnum.Monthly as EVisitorReportTypeEnum | null;

  reportDate = "";

  modalOpen = false;
  modalTitle = "";
  modalMessage = "";
  modalAdditionalMessage = "";
  modalCallback: (() => void) | null = null;

  showModal(
    title: string,
    message: string,
    additionalMessage: string,
    callback: () => void
  ) {
    this.modalTitle = title;
    this.modalMessage = message;
    this.modalAdditionalMessage = additionalMessage;
    this.modalCallback = callback;
    this.modalOpen = true;
  }

  closeModal() {
    this.modalOpen = false;
    this.modalCallback = null;
  }

  confirmAction() {
    if (this.modalCallback) this.modalCallback();
    this.closeModal();
  }

  get allowedExportTypes() {
    switch (this.eVisitorIntegrationId) {
      case EVisitorIntegrationEnum.HR: {
        return [
          {
            name: "Monthly",
            value: EVisitorReportTypeEnum.Monthly,
          },
          {
            name: "Yearly",
            value: EVisitorReportTypeEnum.Yearly,
          },
        ];
      }
      case this.eVisitorIntegrationEnum.SL: {
        return [
          {
            name: "Monthly",
            value: EVisitorReportTypeEnum.Monthly,
          },
        ];
      }
    }

    return [];
  }

  get pickerType() {
    return this.reportType === EVisitorReportTypeEnum.Monthly
      ? {
          label: "EVisitor.SelectMonth",
          pickerType: "month",
          activePicker: "MONTH",
        }
      : {
          label: "EVisitor.SelectYear",
          pickerType: "year",
          activePicker: "YEAR",
        };
  }

  get today() {
    return new Date().toISOString().substring(0, 10);
  }

  get display() {
    if (!this.reportDate) return "";
    return this.$d(new Date(this.reportDate), this.pickerType.pickerType);
  }

  @Watch("reportType")
  reportTypeChanged(
    fresh: EVisitorReportTypeEnum,
    old: EVisitorReportTypeEnum
  ) {
    if (this.reportDate && fresh !== old) {
      if (
        fresh === EVisitorReportTypeEnum.Yearly &&
        old === EVisitorReportTypeEnum.Monthly
      ) {
        const year = new Date(this.reportDate).getFullYear();
        this.reportDate = (year - 1).toString();
      }
      if (
        fresh === EVisitorReportTypeEnum.Monthly &&
        old === EVisitorReportTypeEnum.Yearly
      ) {
        const today = new Date();
        const tempDate = new Date(this.reportDate);
        const tempYear = tempDate.getFullYear() + 1;
        if (tempYear <= today.getFullYear()) {
          tempDate.setFullYear(tempYear);
        }
        const previousMonth = today.getMonth() - 1;

        if (previousMonth < 0) {
          tempDate.setFullYear(tempDate.getFullYear());
          tempDate.setMonth(11);
        } else {
          tempDate.setMonth(previousMonth);
        }
        this.reportDate = tempDate.toISOString().substring(0, 7);
      }
    }
  }

  dateSelected(value: string) {
    this.reportDate = value;
  }

  get period() {
    if (this.reportDate && this.reportType) {
      const dateFrom = new Date(this.reportDate);
      let dateTo = new Date(this.reportDate);

      switch (this.reportType) {
        case EVisitorReportTypeEnum.Monthly: {
          dateTo = new Date(
            Date.UTC(
              dateFrom.getFullYear(),
              dateFrom.getMonth() + 1,
              dateFrom.getDate() - 1
            )
          );
          break;
        }
        case EVisitorReportTypeEnum.Yearly: {
          dateTo = new Date(
            Date.UTC(
              dateFrom.getFullYear() + 1,
              dateFrom.getMonth(),
              dateFrom.getDate() - 1
            )
          );
          break;
        }
      }

      return { dateFrom: dateFrom, dateTo: dateTo };
    }

    return { dateFrom: null, dateTo: null };
  }

  @Ref("formComponent") formComponent?: { submit: () => void };
  isFormValid = false;

  submit() {
    if (this.formComponent) {
      this.formComponent.submit();
    }
  }

  exportReport(
    request: any,
    responseType: ResponseType | undefined,
    successTextResourceKey: string,
    overrides: string[] = []
  ) {
    const handleCodes = ["EVisitor.reportGenerateInfo"];
    const formatBrokenRuleNames = (x: string[]) =>
      x
        .map((h) => this.$t(h).toString())
        .map((th) => (th.endsWith(".") ? th : th + "."))
        .join(" ");
    const adapter = brokenRuleAdapter(this.axios, handleCodes);

    const isOverriding =
      overrides.length !== 0 &&
      this.eVisitorIntegrationId === EVisitorIntegrationEnum.HR;

    this.loading = true;
    this.axios
      .post(
        "reports/EVisitorServiceReport",
        { ...request, overrideRules: overrides },
        {
          responseType: responseType,
          adapter: isOverriding ? undefined : adapter,
        }
      )
      .then((res) => {
        if (responseType === "blob") {
          const groups = /filename=(?<filename>[^;]+);/.exec(
            res.headers["content-disposition"]
          )?.groups;
          saveFile(res.data, groups?.filename ?? "Report.xlsx");
        }
        if (successTextResourceKey) {
          this.$store.dispatch("showNotification", {
            color: "success",
            message: successTextResourceKey,
            localize: true,
            timeout: 5000,
          } as Notification);
        }
      })
      .catch((error) => {
        if (error && error.isHandleBrokenRuleResult && !isOverriding) {
          this.modalMessage = `${formatBrokenRuleNames(error.handled)}`;
          this.modalAdditionalMessage = this.$t(
            "EVisitor.reportGenerateInfoAdditional"
          ).toString();
          this.showModal(
            this.$t("EVisitor.TouristBoardReport", {
              eVisitorServiceName: this.eVisitorIntegrationName,
            }).toString(),
            this.modalMessage,
            this.modalAdditionalMessage,
            () => {
              this.exportReport(
                request,
                responseType,
                successTextResourceKey,
                error.handled
              );
            }
          );
        } else {
          this.$store.dispatch("showNotification", {
            color: "error",
            message: `${formatBrokenRuleNames(error.unhandled)}`,
            timeout: 5000,
          } as Notification);
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  @Getter("appParams/tenantEVisitorIntegration")
  eVisitorIntegration?: EVisitorIntegrationModel;
  eVisitorIntegrationEnum = EVisitorIntegrationEnum;
  get eVisitorIntegrationId() {
    return this.eVisitorIntegration?.id ?? EVisitorIntegrationEnum.None;
  }
  get eVisitorIntegrationName() {
    return this.eVisitorIntegration?.name ?? EVisitorIntegrationNameEnum.None;
  }

  @Watch("eVisitorIntegrationId", { immediate: true })
  eVisitorIntegrationWatch() {
    this.$nextTick(() => {
      if (this.allowedExportTypes.length > 0) {
        this.reportType = this.allowedExportTypes[0].value;
      } else {
        this.reportType = null;
      }
    });
  }

  @Watch("dialogOpen")
  openWatch(fresh: boolean) {
    if (fresh) {
      if (this.allowedExportTypes.length > 0)
        this.reportType = this.allowedExportTypes[0].value;

      const today = new Date();
      if (this.reportType === EVisitorReportTypeEnum.Monthly) {
        today.setMonth(today.getMonth() - 1);
        this.reportDate = today.toISOString().substring(0, 7);
      } else {
        this.reportDate = today.toISOString().substring(0, 4);
      }
    }
  }
}
